import { useCallback, useEffect, useState } from "react";
import config from "../config/index.js";
import { ackJurisdiction, fetchJurisdictionAck } from "../services/api.js";

const STORAGE_KEY = "gilder.jurisdiction.ack.v1";
const RESTRICTED = (config.restrictedRegions || []).map((c) => c.toUpperCase());
// Mirror of the geofence build-time dev bypass. When the env var is
// set to "true", we short-circuit the ack gate entirely — the modal
// never opens. Kept symmetric with `useGeofence.js` so a dev env can
// disable BOTH layers with a single env flag.
const GEOFENCE_DISABLED = import.meta.env.VITE_GEOFENCE_DISABLED === "true";

/**
 * Tracks whether the visitor has acknowledged the V6.2 §A jurisdictional
 * disclaimer + risk disclosure. Acknowledgement is stored locally so the
 * modal doesn't reappear on every page load, and — when a wallet is
 * connected — also persisted server-side so the backend has a soft audit
 * record. The modal does NOT replace the geofence check (`useGeofence`);
 * it is an additional ack-gate the user must clear before reaching any
 * action surface.
 *
 * Returns: { ack, needsAck, save, clear, isSaving }
 *   - ack: the stored ack object ({ jurisdictionCode, risk, timestamp }) or null
 *   - needsAck: true if the visitor has not acknowledged yet
 *   - save({ jurisdictionCode, riskDisclosureAcknowledged }, walletAddress?):
 *       persists locally + optionally hits the backend
 *   - clear(): resets local ack (used by Settings -> "Reset jurisdiction")
 */
export function useJurisdictionAck(walletAddress) {
    const [ack, setAck] = useState(() => readLocal());
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!walletAddress || ack) return;
        let cancelled = false;
        (async () => {
            try {
                const remote = await fetchJurisdictionAck(walletAddress);
                if (cancelled) return;
                if (remote && remote.acknowledged) {
                    const next = {
                        jurisdictionCode: remote.jurisdictionCode || null,
                        riskDisclosureAcknowledged: Boolean(remote.riskDisclosureAcknowledged),
                        timestamp: Date.now(),
                        source: "remote"
                    };
                    writeLocal(next);
                    setAck(next);
                }
            } catch {
                // Backend unreachable — local state still works as the gate.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [walletAddress, ack]);

    const save = useCallback(
        async ({ jurisdictionCode, riskDisclosureAcknowledged }, address) => {
            const code = jurisdictionCode ? String(jurisdictionCode).toUpperCase() : null;
            if (code && RESTRICTED.includes(code)) {
                throw new Error(`Service not available in ${code}.`);
            }
            const next = {
                jurisdictionCode: code,
                riskDisclosureAcknowledged: Boolean(riskDisclosureAcknowledged),
                timestamp: Date.now(),
                source: address ? "wallet" : "local"
            };
            writeLocal(next);
            setAck(next);
            if (address) {
                setIsSaving(true);
                try {
                    await ackJurisdiction(address, {
                        jurisdictionCode: code,
                        riskDisclosureAcknowledged: Boolean(riskDisclosureAcknowledged)
                    });
                } catch {
                    // Soft-fail — local ack still gates the UI. Backend
                    // persistence will retry on next save.
                } finally {
                    setIsSaving(false);
                }
            }
            return next;
        },
        []
    );

    const clear = useCallback(() => {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            /* private mode — ignore */
        }
        setAck(null);
    }, []);

    return {
        ack,
        // When the build-time geofence bypass is on, the disclaimer
        // gate is also bypassed (dev / staging convenience). In every
        // other environment the user must complete the ack.
        needsAck: !GEOFENCE_DISABLED && !ack,
        save,
        clear,
        isSaving
    };
}

function readLocal() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || !data.timestamp) return null;
        return data;
    } catch {
        return null;
    }
}

function writeLocal(data) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
        /* private mode — ignore */
    }
}
